import axios from "axios";
import moment from "moment";
import {
  aggregateMealsByDay,
  getFinalKcalAndPercentage,
} from "../../../helpers/mealsHelper";
import i18n from "../../../i18n";
import {
  getBaseDateFormat,
  getExtendedDateSmallFormat,
} from "../../../modules/constants";
import { handleHttpErrors } from "../../../modules/http";
import { getClaims, getUserProfile } from "../../../modules/login";
import {
  orderType,
  tablePageSizes,
} from "../../ui-library/CustomTable/constants";

// Constants
const LOADING_TABLE_DATA = "LOADING_TABLE_DATA";
const SET_TABLE_DATA = "SET_TABLE_DATA";
const RESET_STATE = "RESET_STATE";
const LOGOUT = "LOGOUT";
const SET_PARTICIPANT_INFO = "SET_PARTICIPANT_INFO";

//Acessors
const getTotalItems = (state) => state?.totalItems;
const getEntries = (state) => state?.entries;
const getSearchedParams = (state) => state?.searchedParams;
const getLoading = (state) => state?.loading;
const getRoleName = (state) => getUserProfile(state);
const getParticipant = (state) => state?.participant;

const canViewDetailMealLog = (state) =>
  getClaims(state, "mealLog.detail.canView");
const canCreateChangeRequestMealLog = (state) =>
  getClaims(state, "mealLog.changeRequest.canCreate");
const canChangeStudyStatus = (state) =>
  getClaims(state, "study.status.canChange");
const canViewActionButton = (state) =>
  getClaims(state, "entriesTable.actionButton.canView");
const canViewStudyStatus = (state) =>
  getClaims(state, "study.status.history.canView");
const canEditParticipant = (state) => getClaims(state, "participant.canEdit");
const canGenerateReport = (state) =>
  getClaims(state, "participant.report.canGenerate");
const canManageUsers = (state) =>
  getClaims(state, "participant.canManageCaregivers");
const canDeleteMeal = (state) => getClaims(state, "mealLog.canSoftDelete");
const canCreate24hRecall = (state) =>
  getClaims(state, "mealLog.entries.canCreateRecall");
const canCreatePaperRecapture = (state) =>
  getClaims(state, "mealLog.entries.canCreatePaperRecapture");

// Default State
const defaultState = {
  entries: [],
  totalItems: null,
  loading: false,
  searchedParams: {
    page: 0,
    limit: tablePageSizes[3],
    search: "",
    orderBy: "created",
    order: orderType.DESC,
  },
  participant: null,
};

//Reducer
export const studyEntriesTableReducer = (state = defaultState, action) => {
  switch (action.type) {
    case LOADING_TABLE_DATA:
      return {
        ...state,
        loading: true,
      };
    case SET_TABLE_DATA: {
      const { tableData, searchedParams } = action;
      return Object.assign({}, state, {
        entries: tableData.total,
        totalItems: tableData.totalEntries,
        searchedParams: searchedParams,
        loading: false,
      });
    }
    case SET_PARTICIPANT_INFO:
      const { participant } = action;
      return {
        ...state,
        participant: participant,
      };
    case RESET_STATE:
      return defaultState;
    case LOGOUT:
      return defaultState;
    default:
      return state;
  }
};

const fetchEntriesTableData =
  (searchedParams, startDate, endDate, participantId = null) =>
  (dispatch) => {
    const params = {
      ...searchedParams,
      StartDate: startDate,
      EndDate: endDate,
    };

    if (participantId) {
      params.ParticipantId = participantId;
    }

    dispatch({
      type: LOADING_TABLE_DATA,
    });

    return axios
      .get(`/api/diet/userinfo/entries`, { params })
      .then(({ data: { entries } }) => {
        dispatch({
          type: SET_TABLE_DATA,
          tableData: parseTableData(entries),
          searchedParams: searchedParams,
        });
        return Promise.resolve("Got data latest meal data for you.");
      })
      .catch((error) => {
        return handleHttpErrors(error.request?.status, {
          default: i18n.t("errorText.dietDiary.entries.get"),
        });
      });
  };

const deleteEntryMeal = (formData) => (dispatch) => {
  return axios
    .post(`/api/diet/changerequest`, formData)
    .then(() => {
      return Promise.resolve(`Your request has been submitted`);
    })
    .catch((error) => {
      const defaultMessage = error.response?.data
        ? error.response.data
        : "There was an error while trying to create the change request!";

      return handleHttpErrors(error.request?.status, {
        default: defaultMessage,
      });
    });
};

const parseTableData = (data) => {
  const { total, totalEntries } = data;

  const formattedData = aggregateMealsByDay(
    total,
    getExtendedDateSmallFormat()
  );

  const kcalObj = getFinalKcalAndPercentage(formattedData);

  return { total: kcalObj, totalEntries };
};

const fetchReportEntriesData =
  (startDate, endDate, participantId = null) =>
  (dispatch, getState) => {
    const params = {
      StartDate: startDate,
      EndDate: endDate,
      limit: 9999,
    };

    if (participantId) {
      params.ParticipantId = participantId;
    }

    return axios
      .get(`/api/diet/dailyentries/`, { params })
      .then(({ data }) => {
        const reportData = {
          entries: data.dailyEntries,
          dailyTargetId: data.dailyTargets?.key,
          generatedDate: moment().format(getBaseDateFormat()),
        };
        return Promise.resolve(reportData);
      })
      .catch((error) => {
        const defaultMessage = error.response?.data
          ? error.response.data
          : `There was an error while trying to get your meal data. ${error}`;

        return handleHttpErrors(error.request?.status, {
          default: defaultMessage,
        });
      });
  };

const fetchDailyTargetHistory = (dailyTargetId) => async (dispatch) => {
  const searchParams = {
    page: 0,
    limit: 9999,
    search: "",
    orderBy: "date",
    order: orderType.DESC,
  };

  const params = {
    ...searchParams,
    dailyTargetId: dailyTargetId,
  };

  return axios
    .get(`/api/diet/dailytarget/history`, { params })
    .then(({ data }) => {
      return Promise.resolve(data.data);
    })
    .catch((error) => {
      return handleHttpErrors(error.request?.status, {
        default: i18n.t("errorText.dietDiary.dailyTargets.get"),
      });
    });
};

const getParticipantInfo = (participantId) => (dispatch, getState) => {
  const params = {
    ParticipantId: participantId,
  };

  return axios
    .get(`/api/participant/getInfo`, { params })
    .then(({ data }) => {
      dispatch({ type: SET_PARTICIPANT_INFO, participant: data });
      return Promise.resolve();
    })
    .catch((error) => {
      const defaultMessage = error.response?.data
        ? error.response.data
        : `There was an error while trying to get the participant information.`;

      return handleHttpErrors(error.request?.status, {
        default: defaultMessage,
      });
    });
};

const resetState = () => (dispatch) => {
  dispatch({ type: RESET_STATE });
};

// Maps
export const mapDispatchToProps = {
  fetchEntriesTableData,
  fetchReportEntriesData,
  resetState,
  deleteEntryMeal,
  getParticipantInfo,
  fetchDailyTargetHistory,
};

export const mapStateToProps = (state) => {
  return {
    totalItems: getTotalItems(state),
    entries: getEntries(state),
    searchedParams: getSearchedParams(state),
    canViewDetailMealLog: canViewDetailMealLog(state),
    canCreateChangeRequestMealLog: canCreateChangeRequestMealLog(state),
    canChangeStudyStatus: canChangeStudyStatus(state),
    canViewActionButton: canViewActionButton(state),
    canViewStudyStatus: canViewStudyStatus(state),
    canEditParticipant: canEditParticipant(state),
    canGenerateReport: canGenerateReport(state),
    canManageUsers: canManageUsers(state),
    loading: getLoading(state),
    roleName: getRoleName(state),
    canDeleteMeal: canDeleteMeal(state),
    participant: getParticipant(state),
    canCreate24hRecall: canCreate24hRecall(state),
    canCreatePaperRecapture: canCreatePaperRecapture(state),
  };
};
